import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import useFirestore from "../../../../config/fireBaseHook";
import useSendSchoolNotification from "../../../../config/sendSchoolNotificationHook";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

const StudentChangeForm = ({ schoolId, pathwayEmail, opEmail }) => {
  const { addData } = useFirestore("SchoolChangeForm");
  const { sendNotification, loading: emailLoading } =
    useSendSchoolNotification();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    fromName: "",
    student_name: "",
    dob: "",
    ssid: "",
    grad_year: "",
    prev_school: "",
    stopped_attending: "",
    currently_attending: "",
    attending_date: "",
    schoolName: "",
    signed_name: "",
    pathwayEmail: pathwayEmail || "", // Initialize with prop values
    opEmail: opEmail || "",
    schoolId: schoolId,
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      pathwayEmail: pathwayEmail || "", // Update if props change
      opEmail: opEmail || "",
    }));
  }, [pathwayEmail, opEmail]); // Add to dependency array

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      "fromName",
      "student_name",
      "schoolName",
      "signed_name",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field].trim() === "") {
        newErrors[field] = "This field is required";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before proceeding
    if (!validateForm()) {
      setSnackbarMessage("Please fill in all required fields");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);

    try {
      // Add data to Firestore
      await addData(formData);

      // Send email notification
      const subject = "Student School Change Form Submission";
      const apiFileName = "SchoolChangeForm";

      await sendNotification(formData, subject, apiFileName);

      setLoading(false);
      setSnackbarMessage(
        "Request has been submitted and notification sent successfully",
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      handleClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      setLoading(false);
      setSnackbarMessage(`Error submitting request: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setLoading(false);
    setErrors({}); // Clear errors when closing
    setFormData({
      schoolId: schoolId,
      fromName: "",
      student_name: "",
      dob: "",
      ssid: "",
      grad_year: "",
      prev_school: "",
      stopped_attending: "",
      currently_attending: "",
      attending_date: "",
      schoolName: "",
      signed_name: "",
      pathwayEmail: pathwayEmail || "", // Keep emails on close
      opEmail: opEmail || "", // Keep emails on close
    });
  };

  return (
    <div>
      <Button
        variant="contained"
        color="secondary"
        startIcon={<FontAwesomeIcon icon={faPlus} />}
        onClick={handleClickOpen}
        sx={{ backgroundColor: "teal", color: "white" }}
      >
        Student School Change Form
      </Button>
      <Dialog open={open} onClose={handleClose}>
        <ModalDialogTitle onClose={handleClose}>
          Student School Change Form
        </ModalDialogTitle>
        <DialogContent>
          {loading || emailLoading ? (
            <CircularProgress />
          ) : (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="School Name *"
                    name="schoolName"
                    fullWidth
                    required
                    value={formData.schoolName}
                    onChange={handleChange}
                    error={!!errors.schoolName}
                    helperText={errors.schoolName}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="From Name *"
                    name="fromName"
                    fullWidth
                    required
                    value={formData.fromName}
                    onChange={handleChange}
                    error={!!errors.fromName}
                    helperText={errors.fromName}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Student Name *"
                    name="student_name"
                    fullWidth
                    required
                    value={formData.student_name}
                    onChange={handleChange}
                    error={!!errors.student_name}
                    helperText={errors.student_name}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="DOB"
                    name="dob"
                    fullWidth
                    value={formData.dob}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="SSID"
                    name="ssid"
                    fullWidth
                    value={formData.ssid}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Graduation Year"
                    name="grad_year"
                    fullWidth
                    value={formData.grad_year}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Previously Attended School"
                    name="prev_school"
                    fullWidth
                    value={formData.prev_school}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Date Student Stopped Enrollment"
                    name="stopped_attending"
                    fullWidth
                    value={formData.stopped_attending}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="High School Currently Attending"
                    name="currently_attending"
                    fullWidth
                    value={formData.currently_attending}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Date Student Begin Enrollment"
                    name="attending_date"
                    fullWidth
                    value={formData.attending_date}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Typed Name of Person Submitting Form *"
                    name="signed_name"
                    fullWidth
                    required
                    value={formData.signed_name}
                    onChange={handleChange}
                    error={!!errors.signed_name}
                    helperText={errors.signed_name}
                  />
                </Grid>
              </Grid>
            </form>
          )}
        </DialogContent>
        <ModalActions onSave={handleSubmit} saving={loading || emailLoading} saveLabel="Submit" />
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ width: "100%" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{
            backgroundColor: snackbarSeverity === "success" ? "black" : "red",
            color: "white",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default StudentChangeForm;
